// ─────────────────────────────────────────────────────────────
// useAuth
// Extracted from App.js.
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { SUPABASE_URL, SUPABASE_ANON_KEY, sb, authFetch } from "../lib/supabase";

// Bump this when new columns are added to profiles that need to be re-fetched.
// Stale cached profiles without the new field will be cleared on next load.
const PROFILE_CACHE_VERSION = '2'; // bumped for is_gifted + dashboard_layout
const PROFILE_CACHE_VERSION_KEY = 'fos_profile_cache_v';

export function useAuth() {
  const [session, setSession] = useLocalStorage("fos_session", null);
  const [profile, setProfile] = useLocalStorage("fos_profile", null);
  const [authLoading, setAuthLoading] = useState(false);

  // Bust stale profile cache when schema version changes
  // so new columns (is_gifted, dashboard_layout, etc.) are always present.
  (() => {
    try {
      if (localStorage.getItem(PROFILE_CACHE_VERSION_KEY) !== PROFILE_CACHE_VERSION) {
        localStorage.removeItem('fos_profile');
        localStorage.setItem(PROFILE_CACHE_VERSION_KEY, PROFILE_CACHE_VERSION);
      }
    } catch {}
  })();
  const [authError, setAuthError] = useState("");

  // signUp accepts an optional `consent` object so the signup forms can pass
  // through legal acknowledgment data. After a successful signup we PATCH
  // the user's profile row to record WHEN they agreed and WHICH VERSION of
  // each document they agreed to. If the PATCH fails (network blip after
  // auth.users row already created) we log but don't fail signup — the
  // user has a working account; we can re-prompt or backfill later.
  //
  // consent shape: {
  //   agreedToPrivacy: boolean,
  //   agreedToTerms:   boolean,
  //   privacyVersion:  string (ISO date, e.g. "2026-05-10"),
  //   termsVersion:    string,
  // }
  const signUp = async (email, password, displayName, consent = null) => {
    setAuthLoading(true);
    setAuthError("");
    try {
      const signupRes = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const signupData = await signupRes.json();

      if (signupData.error) {
        setAuthError(signupData.error_description || signupData.error);
        setAuthLoading(false);
        return { ok: false };
      }

      // Record consent on the profile row using the freshly-issued token
      // from the signup response itself. Doing this BEFORE signIn avoids
      // any race with state updates, and the signup response already
      // contains everything we need (access_token + user.id).
      if (consent && (consent.agreedToPrivacy || consent.agreedToTerms)) {
        const token  = signupData.access_token;
        const userId = signupData.user?.id || signupData.id;
        if (token && userId) {
          const now = new Date().toISOString();
          const patch = {};
          if (consent.agreedToPrivacy) {
            patch.agreed_to_privacy_at      = now;
            patch.agreed_to_privacy_version = consent.privacyVersion || null;
          }
          if (consent.agreedToTerms) {
            patch.agreed_to_terms_at      = now;
            patch.agreed_to_terms_version = consent.termsVersion || null;
          }
          try {
            await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}`, {
              method: "PATCH",
              headers: {
                apikey: SUPABASE_ANON_KEY,
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                Prefer: "return=minimal",
              },
              body: JSON.stringify(patch),
            });
          } catch (e) {
            console.warn("[useAuth] consent patch failed:", e);
          }
        }
      }

      // Auto sign in
      return await signIn(email, password);
    } catch (e) {
      setAuthError("Connection error");
      setAuthLoading(false);
      return { ok: false };
    }
  };

  const signIn = async (email, password) => {
    setAuthLoading(true); 
    setAuthError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        setAuthError(data.error_description || data.error || "Invalid credentials");
        setAuthLoading(false); 
        return false;
      }

      sb.setToken(data.access_token, data.refresh_token, data.expires_in);
      setSession(data);

      if (data.user?.id) {
        await fetchProfile(data.user.id, data.access_token);
      }

      setAuthLoading(false);
      // After successful login, always land on Dashboard.
      // Use window.location.assign so the URL is updated AND the app
      // gets a clean re-mount with the new authenticated session.
      if (window.location.pathname !== "/") {
        window.location.assign("/");
      }
      return true;
    } catch (e) {
      setAuthError("Could not connect to server");
      setAuthLoading(false); 
      return false;
    }
  };

  const signOut = async () => {
    try {
      await sb.signOut();
      localStorage.clear(); // Clear everything
      setSession(null); 
      setProfile(null);
      window.location.reload(); // Force clean reload
    } catch (e) {
      console.error("Sign out error", e);
    }
  };

  const fetchProfile = async (userId, token) => {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=*`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data?.[0]) setProfile(data[0]);
    } catch (e) {
      console.error("Profile fetch failed", e);
    }
  };

  // Public-facing refetch: reloads the profile using the current session.
  // Call this after any action that changes profile state on the server
  // (Stripe Checkout completion, account restore, webhook-driven changes,
  // re-consent acceptance). Without this, the localStorage cache becomes
  // stale relative to the DB.
  //
  // Uses authFetch so the request runs through ensureValidToken first —
  // critical because Supabase access tokens default to 1-hour TTL. If
  // we used raw fetch + authHeaders(), the request would 401 once the
  // user's session crossed the hour mark, silently breaking gates that
  // depend on refreshProfile to flip state (e.g. ReConsentGate).
  const refreshProfile = async () => {
    const uid = session?.user?.id;
    if (!uid) return;
    try {
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${uid}&select=*`);
      if (!res.ok) {
        console.warn("[useAuth] refreshProfile non-OK:", res.status);
        return;
      }
      const data = await res.json();
      if (data?.[0]) setProfile(data[0]);
    } catch (e) {
      console.error("[useAuth] refreshProfile failed:", e);
    }
  };

  const updateProfile = async (updates) => {
    if (!session?.user?.id) return;
    try {
      // Use authFetch so ensureValidToken refreshes the access_token first.
      // Raw fetch + authHeaders() would silently 401 after token expiry.
      await authFetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${session.user.id}`, {
        method: "PATCH",
        body: JSON.stringify(updates)
      });
      setProfile(prev => ({ ...prev, ...updates }));
    } catch (e) {}
  };

  // Fetch daily note for a specific date
  const getDailyNote = async (date, accountId) => {
    if (!session?.user?.id) return null;
    try {
      const dateStr = date.toISOString().split("T")[0];
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/daily_journal?user_id=eq.${session.user.id}&journal_date=eq.${dateStr}&account_id=eq.${accountId}`
      );
      const data = await res.json();
      return data.length > 0 ? data[0] : null;
    } catch (e) {
      console.error("Failed to fetch daily note:", e);
      return null;
    }
  };

  // Save daily note
  const saveDailyNote = async (date, accountId, notes) => {
    if (!session?.user?.id) return;
    try {
      const dateStr = date.toISOString().split("T")[0];
      const existingNote = await getDailyNote(date, accountId);
      
      if (existingNote) {
        // Update existing
        await authFetch(`${SUPABASE_URL}/rest/v1/daily_journal?id=eq.${existingNote.id}`, {
          method: "PATCH",
          body: JSON.stringify({ notes, updated_at: new Date().toISOString() })
        });
      } else {
        // Create new
        await authFetch(`${SUPABASE_URL}/rest/v1/daily_journal`, {
          method: "POST",
          body: JSON.stringify({
            user_id: session.user.id,
            account_id: accountId,
            journal_date: dateStr,
            notes
          })
        });
      }
    } catch (e) {
      console.error("Failed to save daily note:", e);
    }
  };

  return { 
    session, 
    profile, 
    authLoading, 
    authError, 
    signUp, 
    signIn, 
    signOut, 
    updateProfile,
    refreshProfile,
    getDailyNote,
    saveDailyNote,
    isLoggedIn: !!session?.access_token 
  };
}



// ─────────────────────────────────────────────────────────────
// TRADE EVENT TRACKING UTILITIES
// ─────────────────────────────────────────────────────────────
// ──── AUDIT LOGGING (Currently Unused - Reserved for Future Use) ────
// async function createTradeEvent(tradeId, eventType, oldData, newData, reason = null) {
//   try {
//     const eventData = {
//       trade_id: tradeId,
//       event_type: eventType,
//       old_data: oldData || null,
//       new_data: newData || null,
//       reason: reason || null,
//     };
//     
//     await fetch(`${SUPABASE_URL}/rest/v1/trade_events`, {
//       method: "POST",
//       headers: { ...authHeaders(), Prefer: "return=representation" },
//       body: JSON.stringify(eventData),
//     });
//     
//     console.log(`✓ Event logged: ${eventType} for trade ${tradeId}`);
//   } catch (error) {
//     console.error("Error creating trade event:", error);
//   }
// }

// Detect what changed between old and new trade data
// function detectChanges(oldTrade, newTrade) {
//   const changes = {};
//   const keys = Object.keys(newTrade);
//   
//   for (let key of keys) {
//     if (key === 'id' || key === 'created_at' || key === 'updated_at' || key === 'version') continue;
//     
//     const oldVal = oldTrade?.[key];
//     const newVal = newTrade[key];
//     
//     // Deep compare for arrays
//     const oldStr = JSON.stringify(oldVal);
//     const newStr = JSON.stringify(newVal);
//     
//     if (oldStr !== newStr) {
//       changes[key] = { from: oldVal, to: newVal };
//     }
//   }
//   
//   return changes;
// }

// Determine event type based on changes
// function determineEventType(changes) {
//   if (changes.stop_loss && !changes.take_profit && Object.keys(changes).length === 1) return 'STOP_MOVED';
//   if (changes.take_profit && !changes.stop_loss && Object.keys(changes).length === 1) return 'TP_MOVED';
//   if (changes.notes && Object.keys(changes).length === 1) return 'NOTE_ADDED';
//   if (changes.exit_price || changes.pnl) return 'FULL_EXIT';
//   return 'EDITED';
// }


